import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Calendar, Users, Search, ArrowRight, 
  Clock, Star, MapPin, Loader2, ChevronRight, Sun, 
  Sparkles, Heart, Compass, Globe, Home, Hotel,
  Palmtree, Castle, Tent, Anchor, Umbrella, Mountain,
  Waves, Coffee, Wine, Footprints, Bed, Bath, Wifi,
  Car, Utensils, Dumbbell, Pool, TreePine, Flame, Baby,
  CloudSun
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import TravelConcierge from '../components/travel/TravelConcierge';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';
import PlacesSearch from '../components/places/PlacesSearch';
import PlaceDetails from '../components/places/PlaceDetails';
import WeatherWidget from '../components/weather/WeatherWidget';
import { WeatherCard, WeatherRecommendations } from '../components/weather/WeatherIntelligence';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero images for different moods
const HERO_IMAGES = {
  default: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80',
  luxury: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1920&q=80',
  villa: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1920&q=80',
  beach: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1920&q=80',
  mountain: 'https://images.unsplash.com/photo-1601918774946-25832a4be0d6?w=1920&q=80',
  city: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1920&q=80',
};

// Destination backgrounds
const DESTINATION_IMAGES = {
  'Dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80',
  'Bali': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1920&q=80',
  'Maldives': 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1920&q=80',
  'Paris': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
  'Santorini': 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=1920&q=80',
  'Tokyo': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80',
  'New York': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&q=80',
  'Singapore': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=80',
  'Goa': 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1920&q=80',
  'Switzerland': 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1920&q=80',
  'default': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80'
};

// Popular destinations
const POPULAR_DESTINATIONS = [
  { code: 'DEL', city: 'New Delhi', country: 'India' },
  { code: 'BOM', city: 'Mumbai', country: 'India' },
  { code: 'BLR', city: 'Bengaluru', country: 'India' },
  { code: 'GOI', city: 'Goa', country: 'India' },
  { code: 'DXB', city: 'Dubai', country: 'UAE' },
  { code: 'SIN', city: 'Singapore', country: 'Singapore' },
  { code: 'BKK', city: 'Bangkok', country: 'Thailand' },
  { code: 'PAR', city: 'Paris', country: 'France' },
  { code: 'LON', city: 'London', country: 'UK' },
  { code: 'NYC', city: 'New York', country: 'USA' },
  { code: 'BAL', city: 'Bali', country: 'Indonesia' },
  { code: 'MLE', city: 'Maldives', country: 'Maldives' },
];

// Featured Stays
const FEATURED_STAYS = [
  {
    name: 'Overwater Paradise Villa',
    location: 'Maldives',
    type: 'Overwater Villa',
    price: '₹85,000',
    rating: 4.9,
    image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80',
    tag: 'Most Loved',
    amenities: ['Private Pool', 'Ocean View', 'Butler Service']
  },
  {
    name: 'Santorini Cliffside Suite',
    location: 'Greece',
    type: 'Luxury Suite',
    price: '₹42,000',
    rating: 4.8,
    image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&q=80',
    tag: 'Editor\'s Pick',
    amenities: ['Sunset View', 'Infinity Pool', 'Wine Cellar']
  },
  {
    name: 'Ubud Rainforest Retreat',
    location: 'Bali',
    type: 'Eco Resort',
    price: '₹18,000',
    rating: 4.7,
    image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80',
    tag: 'Eco-Friendly',
    amenities: ['Yoga Studio', 'Spa', 'Farm-to-Table']
  },
  {
    name: 'Swiss Alps Chalet',
    location: 'Switzerland',
    type: 'Mountain Chalet',
    price: '₹65,000',
    rating: 4.9,
    image: 'https://images.unsplash.com/photo-1601918774946-25832a4be0d6?w=1920&q=80',
    tag: 'Winter Escape',
    amenities: ['Fireplace', 'Ski Access', 'Hot Tub']
  },
];

// Boutique Hotels
const BOUTIQUE_HOTELS = [
  { name: 'The Oberoi', city: 'Mumbai', price: '₹25,000', image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80', rating: 4.9, style: 'Heritage Luxury' },
  { name: 'Aman Tokyo', city: 'Tokyo', price: '₹55,000', image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80', rating: 4.8, style: 'Minimalist Zen' },
  { name: 'Riad Yasmine', city: 'Marrakech', price: '₹12,000', image: 'https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=800&q=80', rating: 4.7, style: 'Moroccan Charm' },
  { name: 'The Silo', city: 'Cape Town', price: '₹38,000', image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80', rating: 4.8, style: 'Industrial Chic' },
];

// Unique Stays
const UNIQUE_STAYS = [
  { name: 'Treehouse Retreat', location: 'Costa Rica', type: 'Treehouse', price: '₹15,000', image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80', icon: TreePine },
  { name: 'Glass Igloo', location: 'Finland', type: 'Igloo', price: '₹35,000', image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80', icon: Sparkles },
  { name: 'Desert Camp', location: 'Morocco', type: 'Glamping', price: '₹22,000', image: 'https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=800&q=80', icon: Tent },
  { name: 'Floating Bungalow', location: 'Thailand', type: 'Overwater', price: '₹18,000', image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&q=80', icon: Anchor },
];

// Beach Resorts
const BEACH_RESORTS = [
  { name: 'W Maldives', location: 'Maldives', beach: 'Fesdu Island', price: '₹95,000', image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80' },
  { name: 'Amanpuri', location: 'Thailand', beach: 'Phuket', price: '₹75,000', image: 'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=800&q=80' },
  { name: 'Taj Exotica', location: 'Goa', beach: 'Benaulim Beach', price: '₹28,000', image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=800&q=80' },
  { name: 'Four Seasons', location: 'Bali', beach: 'Jimbaran Bay', price: '₹65,000', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80' },
];

// City Hotels
const CITY_HOTELS = [
  { name: 'The Ritz Paris', city: 'Paris', price: '₹1,20,000', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80', landmark: 'Place Vendôme' },
  { name: 'The Plaza', city: 'New York', price: '₹85,000', image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&q=80', landmark: 'Central Park' },
  { name: 'Marina Bay Sands', city: 'Singapore', price: '₹45,000', image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80', landmark: 'Infinity Pool' },
  { name: 'Burj Al Arab', city: 'Dubai', price: '₹1,50,000', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80', landmark: 'Jumeirah Beach' },
];

// Family Resorts
const FAMILY_RESORTS = [
  { name: 'Atlantis The Palm', city: 'Dubai', attraction: 'Aquaventure Waterpark', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80', tag: 'Kids Love It' },
  { name: 'Club Med Bali', city: 'Bali', attraction: 'Kids Club & Activities', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80', tag: 'All-Inclusive' },
  { name: 'Leela Palace', city: 'Udaipur', attraction: 'Lake Palace Views', image: 'https://images.unsplash.com/photo-1568495248636-6432b97bd949?w=800&q=80', tag: 'Heritage' },
  { name: 'Anantara', city: 'Maldives', attraction: 'Dolphin Cruises', image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80', tag: 'Adventure' },
];

// Wellness Retreats
const WELLNESS_RETREATS = [
  { name: 'Ananda Spa', location: 'Himalayas, India', focus: 'Ayurveda & Yoga', image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80', duration: '7 Days' },
  { name: 'Como Shambhala', location: 'Bali', focus: 'Holistic Wellness', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80', duration: '5 Days' },
  { name: 'SHA Wellness', location: 'Spain', focus: 'Detox & Anti-Aging', image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80', duration: '10 Days' },
];

// Travel Stories / Editorial
const STAY_STORIES = [
  {
    title: "The World's Most Romantic Overwater Villas",
    author: "Aira Travel",
    image: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1200&q=80",
    readTime: "6 min read",
    category: "Honeymoon"
  },
  {
    title: "Hidden Boutique Hotels of Southeast Asia",
    author: "Aira Travel",
    image: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=1200&q=80",
    readTime: "8 min read",
    category: "Hidden Gems"
  },
  {
    title: "Eco-Luxury: Sustainable Stays That Don't Compromise",
    author: "Aira Travel",
    image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80",
    readTime: "5 min read",
    category: "Sustainable"
  },
];

// Section Header Component
const SectionHeader = ({ eyebrow, title, subtitle, accentColor = '#00E5FF', align = 'left' }) => (
  <div className={`mb-12 ${align === 'center' ? 'text-center' : ''}`}>
    <p className="text-sm uppercase tracking-[0.3em] mb-3 font-medium" style={{ color: accentColor }}>
      {eyebrow}
    </p>
    <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white font-bold mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-white/50 max-w-2xl">{subtitle}</p>}
  </div>
);

// Stay Card Component
const StayCard = ({ stay, size = 'normal' }) => {
  const sizeClasses = {
    large: 'md:col-span-2 md:row-span-2 min-h-[450px]',
    normal: 'min-h-[300px]',
    wide: 'md:col-span-2 min-h-[250px]'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className={`relative overflow-hidden rounded-2xl cursor-pointer group ${sizeClasses[size]}`}
      data-testid={`stay-card-${stay.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <img 
        src={stay.image}
        alt={stay.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      
      {stay.tag && (
        <div className="absolute top-4 left-4">
          <span className="bg-[#FF007F] text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,0,127,0.5)]">
            {stay.tag}
          </span>
        </div>
      )}

      {stay.rating && (
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-black/50 backdrop-blur-xl px-3 py-1 rounded-full">
          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          <span className="text-white text-sm font-medium">{stay.rating}</span>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6">
        <p className="text-white/60 text-sm uppercase tracking-wider mb-1">{stay.type || stay.style}</p>
        <h3 className={`font-['Playfair_Display'] text-white font-bold mb-1 ${size === 'large' ? 'text-3xl' : 'text-2xl'}`}>
          {stay.name}
        </h3>
        <p className="text-white/60 flex items-center gap-1 mb-3">
          <MapPin className="w-4 h-4" /> {stay.location || stay.city}
        </p>
        
        {stay.amenities && (
          <div className="flex flex-wrap gap-2 mb-3">
            {stay.amenities.slice(0, 3).map((amenity, i) => (
              <span key={i} className="text-xs text-white/70 bg-white/10 px-2 py-1 rounded-full">{amenity}</span>
            ))}
          </div>
        )}
        
        {stay.price && (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#00E5FF] text-xl font-bold">{stay.price}</p>
              <p className="text-white/40 text-xs">per night</p>
            </div>
            <div className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center group-hover:bg-[#00E5FF] transition-all shadow-[0_0_20px_rgba(0,0,0,0.3)]">
              <ArrowRight className="w-5 h-5 text-white" />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Weather Search Section Component
const WeatherSearchSection = ({ onWeatherLoad }) => {
  const [destination, setDestination] = useState('');
  const [showWeather, setShowWeather] = useState(false);
  
  const popularDestinations = ['Dubai', 'Paris', 'Tokyo', 'New York', 'London', 'Bali', 'Singapore', 'Sydney'];
  
  const handleSearch = () => {
    if (destination.trim()) {
      setShowWeather(true);
    }
  };
  
  const handleDestinationClick = (dest) => {
    setDestination(dest);
    setShowWeather(true);
  };
  
  return (
    <div className="space-y-6">
      {/* Search Input */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Enter destination for weather forecast..."
            className="w-full pl-12 pr-4 py-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-gold)] transition-colors text-lg"
            data-testid="weather-destination-input"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={!destination.trim()}
          className="px-8 py-4 rounded-xl bg-gradient-to-r from-[var(--color-gold)] to-amber-500 text-black font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          data-testid="weather-search-button"
        >
          <CloudSun className="w-5 h-5" />
          Get Weather
        </button>
      </div>
      
      {/* Popular Destinations */}
      <div className="flex flex-wrap gap-2">
        <span className="text-[var(--text-secondary)] text-sm">Popular:</span>
        {popularDestinations.map((dest) => (
          <button
            key={dest}
            onClick={() => handleDestinationClick(dest)}
            className="px-4 py-2 rounded-full bg-[var(--bg-card)] text-[var(--text-secondary)] text-sm hover:text-[var(--color-gold)] hover:border-[var(--color-gold)] border border-[var(--border-primary)] transition-colors"
          >
            {dest}
          </button>
        ))}
      </div>
      
      {/* Weather Card */}
      {showWeather && destination && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8"
        >
          <WeatherCard destination={destination} onWeatherLoad={onWeatherLoad} />
        </motion.div>
      )}
    </div>
  );
};

export default function StaysPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('featured');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [heroImage, setHeroImage] = useState(HERO_IMAGES.default);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [destinationWeather, setDestinationWeather] = useState(null);
  
  // Form states
  const [searchForm, setSearchForm] = useState({
    destination: '',
    checkIn: '',
    checkOut: '',
    guests: 2,
    rooms: 1
  });

  // Handle hotel selection and navigate to booking
  const handleSelectHotel = async (hotel) => {
    // First, call pricing API to get reviewHash
    try {
      setLoading(true);
      toast.info('Getting room details...');
      
      const pricingResponse = await axios.post(
        `${API_URL}/api/tripjack/hotels/pricing`,
        {
          correlation_id: hotel.correlationId,
          hotel_id: String(hotel.hotelId || hotel.id),
          checkin_date: searchForm.checkIn,
          checkout_date: searchForm.checkOut,
          adults: searchForm.guests,
          children: 0,
          rooms: searchForm.rooms || 1
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (!pricingResponse.data.success) {
        toast.error('Failed to get room pricing. Please try again.');
        setLoading(false);
        return;
      }

      const pricingData = pricingResponse.data;
      const reviewHash = pricingData.data?.reviewHash;
      const pricingOptions = pricingData.data?.options || [];
      const firstOption = pricingOptions[0] || {};
      const optionId = firstOption.optionId || hotel.optionId;
      const totalPrice = firstOption.pricing?.totalPrice || hotel.price;

      if (!reviewHash) {
        toast.error('Unable to proceed. Please try again.');
        setLoading(false);
        return;
      }

      // Navigate to complete-booking with correct state structure
      navigate('/complete-booking', {
        state: {
          type: 'hotel',
          selection: {
            correlationId: hotel.correlationId,
            hotelId: String(hotel.hotelId || hotel.id),
            optionId: optionId,
            reviewHash: reviewHash,
            name: hotel.name,
            city: hotel.city || hotel.location,
            stars: hotel.stars || hotel.rating || 4,
            price: totalPrice,
            currency: hotel.currency || 'INR',
            roomType: hotel.roomType,
            mealBasis: hotel.mealBasis,
            isRefundable: hotel.isRefundable,
            checkinDate: searchForm.checkIn,
            checkoutDate: searchForm.checkOut,
            adults: searchForm.guests,
            rooms: searchForm.rooms || 1
          },
          pricingData: pricingData.data
        }
      });
    } catch (error) {
      console.error('Pricing error:', error);
      toast.error('Failed to get room details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update hero based on destination
  useEffect(() => {
    const dest = searchForm.destination;
    if (dest) {
      const matchedDest = Object.keys(DESTINATION_IMAGES).find(
        key => dest.toLowerCase().includes(key.toLowerCase())
      );
      if (matchedDest) {
        setHeroImage(DESTINATION_IMAGES[matchedDest]);
      }
    }
  }, [searchForm.destination]);

  const filterDestinations = (query) => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return POPULAR_DESTINATIONS.filter(
      d => d.code.toLowerCase().includes(q) || 
           d.city.toLowerCase().includes(q) ||
           d.country.toLowerCase().includes(q)
    ).slice(0, 6);
  };

  const searchHotels = async () => {
    if (!searchForm.destination || !searchForm.checkIn || !searchForm.checkOut) {
      toast.error('Please fill in destination, check-in and check-out dates');
      return;
    }

    if (new Date(searchForm.checkOut) <= new Date(searchForm.checkIn)) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    setLoading(true);
    setSearched(true);
    setResults([]);

    try {
      // Use TripJack Hotel API
      const cityCode = searchForm.destination.toUpperCase().substring(0, 3);
      const response = await axios.get(
        `${API_URL}/api/tripjack/hotels/search`,
        {
          params: {
            city_code: cityCode,
            checkin_date: searchForm.checkIn,
            checkout_date: searchForm.checkOut,
            adults: searchForm.guests,
            children: 0
          },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.data.success) {
        const tripjackHotels = response.data.data?.hotels || [];
        const correlationId = response.data.data?.correlationId || response.data.correlation_id;
        
        // Format hotels for display - TripJack v3 API structure
        const formattedHotels = tripjackHotels.map(h => {
          const firstOption = h.options?.[0];
          const pricing = firstOption?.pricing || {};
          const roomInfo = firstOption?.roomInfo?.[0] || {};
          
          return {
            id: h.hotelId || h.id,
            hotelId: h.hotelId || h.id,  // Include hotelId for booking
            name: h.name || 'Hotel',
            location: searchForm.destination,
            city: searchForm.destination,
            rating: 8.5, // TripJack doesn't return ratings in search
            stars: 4, // Default stars
            image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
            price: pricing.totalPrice || 0,
            currency: pricing.currency || 'INR',
            roomType: roomInfo.name || 'Standard Room',
            mealBasis: firstOption?.mealBasis || 'Room Only',
            isRefundable: firstOption?.cancellation?.isRefundable || false,
            amenities: ['WiFi', 'AC', 'Restaurant'],
            source: 'TripJack',
            optionId: firstOption?.optionId,
            correlationId: correlationId  // Include correlationId for booking flow
          };
        });
        
        setResults(formattedHotels);
        if (formattedHotels.length > 0) {
          toast.success(`Found ${formattedHotels.length} hotels`);
        } else {
          toast.info('No hotels found for this destination');
        }
      } else {
        toast.error(response.data.error || 'No hotels found');
        setResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search hotels. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Handler for AI Concierge stay search
  const handleConciergeStaySearch = (params) => {
    // Update search form with concierge-parsed data
    setSearchForm({
      destination: params.destination || '',
      checkIn: params.check_in || '',
      checkOut: params.check_out || '',
      guests: params.guests || 2,
      rooms: params.rooms || 1
    });
    // Auto-trigger search after a brief delay to let state update
    setTimeout(() => {
      searchHotels();
    }, 100);
  };

  // Category tabs
  const categoryTabs = [
    { id: 'featured', label: 'Featured', icon: Star },
    { id: 'explore', label: 'Explore Places', icon: Compass },
    { id: 'weather', label: 'Weather Intelligence', icon: CloudSun },
    { id: 'boutique', label: 'Boutique', icon: Hotel },
    { id: 'unique', label: 'Unique', icon: Sparkles },
    { id: 'beach', label: 'Beach', icon: Umbrella },
    { id: 'city', label: 'City', icon: Building2 },
    { id: 'family', label: 'Family', icon: Baby },
    { id: 'wellness', label: 'Wellness', icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-[#09080E]" data-testid="stays-page">
      {/* Floating Back Button */}
      <FloatingBackButton />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative h-[90vh] min-h-[700px] overflow-hidden">
        {/* Dynamic Background */}
        <motion.div 
          key={heroImage}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/70 via-transparent to-[#09080E]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09080E]/60 via-transparent to-[#09080E]/60" />
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 px-4 md:px-16">
          {/* Editorial Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <p className="text-[#FF007F] text-sm uppercase tracking-[0.3em] mb-4">
              Travel & Stay
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight max-w-4xl">
              Where Every Stay <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#00E5FF] to-[#FF007F]">
                Becomes a Story
              </span>
            </h1>
            <p className="text-white/60 text-lg mt-4 max-w-xl">
              Handpicked accommodations that transform ordinary trips into extraordinary memories
            </p>
          </motion.div>

          {/* Glass Search Widget */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl max-w-5xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Destination */}
              <div className="relative lg:col-span-1">
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Destination</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FF007F]" />
                  <input
                    type="text"
                    value={searchForm.destination}
                    onChange={(e) => {
                      setSearchForm({...searchForm, destination: e.target.value});
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Where to?"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] transition-all"
                    data-testid="destination-input"
                  />
                  {showSuggestions && filterDestinations(searchForm.destination).length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#12101A] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                      {filterDestinations(searchForm.destination).map((dest) => (
                        <button
                          key={dest.code}
                          onClick={() => {
                            setSearchForm({...searchForm, destination: dest.city});
                            setShowSuggestions(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-white font-medium">{dest.city}</span>
                              <span className="text-white/50 ml-2 text-sm">{dest.country}</span>
                            </div>
                            <span className="text-[#FF007F] font-mono text-sm">{dest.code}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Check-in */}
              <div className="lg:col-span-1">
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Check-in</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input 
                    type="date" 
                    value={searchForm.checkIn} 
                    onChange={(e) => setSearchForm({...searchForm, checkIn: e.target.value})} 
                    min={today} 
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white [color-scheme:dark]"
                    data-testid="checkin-input"
                  />
                </div>
              </div>

              {/* Check-out */}
              <div className="lg:col-span-1">
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Check-out</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input 
                    type="date" 
                    value={searchForm.checkOut} 
                    onChange={(e) => setSearchForm({...searchForm, checkOut: e.target.value})} 
                    min={searchForm.checkIn || today} 
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white [color-scheme:dark]"
                    data-testid="checkout-input"
                  />
                </div>
              </div>

              {/* Guests */}
              <div className="lg:col-span-1">
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Guests</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <select 
                    value={searchForm.guests} 
                    onChange={(e) => setSearchForm({...searchForm, guests: parseInt(e.target.value)})} 
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white appearance-none cursor-pointer"
                    data-testid="guests-input"
                  >
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <option key={n} value={n} className="bg-[#12101A]">{n} Guest{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search Button */}
              <div className="lg:col-span-1 flex items-end">
                <button 
                  onClick={searchHotels} 
                  disabled={loading} 
                  className="w-full bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold py-3 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="search-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                  Search
                </button>
              </div>
            </div>

            {/* Quick Destinations */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-white/40 text-sm mb-3">Popular destinations</p>
              <div className="flex flex-wrap gap-2">
                {['Dubai', 'Bali', 'Maldives', 'Paris', 'Goa', 'Singapore'].map(city => (
                  <button
                    key={city}
                    onClick={() => setSearchForm({...searchForm, destination: city})}
                    className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20 hover:text-white transition-all text-sm"
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== SEARCH RESULTS ==================== */}
      <AnimatePresence>
        {searched && (
          <motion.section 
            initial={{ opacity: 0, y: 50 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0 }} 
            className="px-4 md:px-16 py-16 bg-[#09080E]"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-[#FF007F] animate-spin mb-4" />
                <p className="text-white/60">Searching for the perfect stays...</p>
              </div>
            ) : results.length > 0 ? (
              <>
                <SectionHeader eyebrow="Search Results" title={`${results.length} Stays Found`} accentColor="#FF007F" />
                <div className="grid gap-6">
                  {results.map((hotel, index) => (
                    <motion.div 
                      key={hotel.id || index} 
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ delay: index * 0.05 }} 
                      className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF007F]/30 transition-all group"
                    >
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-72 h-48 md:h-auto relative overflow-hidden">
                          <img 
                            src={hotel.image || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80'} 
                            alt={hotel.name} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                          />
                          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-xl px-3 py-1 rounded-full">
                            <div className="flex items-center gap-1">
                              {[...Array(hotel.stars || hotel.rating || 4)].map((_, i) => (
                                <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-white text-xl font-semibold mb-1">{hotel.name}</h3>
                              <p className="text-white/50 flex items-center gap-1">
                                <MapPin className="w-4 h-4" /> {hotel.city || hotel.location}
                              </p>
                              {hotel.roomType && (
                                <p className="text-[#00E5FF] text-sm mt-1">{hotel.roomType}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {hotel.isRefundable && (
                                <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">
                                  Free Cancellation
                                </span>
                              )}
                              {hotel.mealBasis && (
                                <span className="bg-white/10 text-white/70 text-xs px-2 py-1 rounded-full">
                                  {hotel.mealBasis}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-white/40 text-sm">Total Price</p>
                              <p className="text-[#FF007F] text-3xl font-bold">
                                {hotel.currency === 'INR' ? '₹' : hotel.currency} {hotel.price?.toLocaleString('en-IN')}
                              </p>
                              <p className="text-white/40 text-sm">for your stay</p>
                            </div>
                            <button 
                              onClick={() => handleSelectHotel(hotel)}
                              className="bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white px-6 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,0,127,0.4)] transition-all"
                              data-testid={`view-rooms-${hotel.hotelId || hotel.id}`}
                            >
                              View Rooms
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Building2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-white text-xl font-semibold mb-2">No Stays Found</h3>
                <p className="text-white/50">Try different dates or destinations</p>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ==================== CATEGORY TABS ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-8 bg-[#09080E] sticky top-0 z-40">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {categoryTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveCategory(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap backdrop-blur-xl ${
                  activeCategory === tab.id
                    ? 'bg-gradient-to-r from-[#FF007F]/30 to-[#00E5FF]/30 text-white border border-white/20 shadow-[0_0_25px_rgba(255,0,127,0.3)]'
                    : 'bg-white/[0.05] text-white/60 border border-white/[0.08] hover:bg-white/[0.1] hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ==================== FEATURED STAYS ==================== */}
      {!searched && activeCategory === 'featured' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Handpicked for You" title="Featured Stays" subtitle="Our editors' top picks for unforgettable experiences" accentColor="#FF007F" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURED_STAYS.map((stay, index) => (
              <StayCard key={stay.name} stay={stay} size={index === 0 ? 'large' : 'normal'} />
            ))}
          </div>
        </section>
      )}

      {/* ==================== BOUTIQUE HOTELS ==================== */}
      {!searched && activeCategory === 'boutique' && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader eyebrow="Intimate & Unique" title="Boutique Hotels" subtitle="Character-filled properties with distinctive personalities" accentColor="#FFD700" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BOUTIQUE_HOTELS.map((hotel, index) => (
              <motion.div 
                key={hotel.name} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.1 }} 
                className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:border-[#FFD700]/30 transition-all"
              >
                <div className="h-56 relative overflow-hidden">
                  <img src={hotel.image} alt={hotel.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#FFD700]/90 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{hotel.style}</span>
                  </div>
                  <div className="absolute top-4 right-4 flex items-center gap-1 bg-black/50 backdrop-blur-xl px-2 py-1 rounded-full">
                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    <span className="text-white text-xs">{hotel.rating}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-['Playfair_Display'] text-white text-xl font-bold mb-1">{hotel.name}</h3>
                  <p className="text-white/60 text-sm mb-3">{hotel.city}</p>
                  <p className="text-[#FFD700] font-semibold">{hotel.price}<span className="text-white/40 text-sm font-normal"> /night</span></p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== UNIQUE STAYS ==================== */}
      {!searched && activeCategory === 'unique' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Beyond Ordinary" title="Unique Stays" subtitle="One-of-a-kind accommodations you won't find anywhere else" accentColor="#00E5FF" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {UNIQUE_STAYS.map((stay, index) => (
              <motion.div 
                key={stay.name} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.1 }} 
                className="relative overflow-hidden rounded-2xl cursor-pointer group aspect-[3/4]"
              >
                <img src={stay.image} alt={stay.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 w-12 h-12 bg-[#00E5FF]/20 backdrop-blur-xl rounded-full flex items-center justify-center">
                  <stay.icon className="w-6 h-6 text-[#00E5FF]" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-[#00E5FF] text-xs uppercase tracking-wider">{stay.type}</span>
                  <h3 className="font-['Playfair_Display'] text-white text-xl font-bold mt-1 mb-1">{stay.name}</h3>
                  <p className="text-white/60 text-sm mb-2">{stay.location}</p>
                  <p className="text-[#00E5FF] font-semibold">{stay.price}<span className="text-white/40 text-sm font-normal"> /night</span></p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== BEACH RESORTS ==================== */}
      {!searched && activeCategory === 'beach' && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader eyebrow="Sun, Sand & Sea" title="Beach Resorts" subtitle="Wake up to the sound of waves in paradise" accentColor="#00E5FF" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BEACH_RESORTS.map((resort, index) => (
              <motion.div 
                key={resort.name} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.1 }} 
                className="relative overflow-hidden rounded-2xl cursor-pointer group min-h-[350px]"
              >
                <img src={resort.image} alt={resort.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#00E5FF]/80 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{resort.beach}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold mb-1">{resort.name}</h3>
                  <p className="text-white/60 text-sm mb-3">{resort.location}</p>
                  <p className="text-[#00E5FF] text-xl font-bold">{resort.price}<span className="text-white/40 text-sm font-normal"> /night</span></p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== CITY HOTELS ==================== */}
      {!searched && activeCategory === 'city' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Urban Luxury" title="City Hotels" subtitle="Iconic properties in the world's greatest cities" accentColor="#FF007F" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {CITY_HOTELS.map((hotel, index) => (
              <motion.div 
                key={hotel.name} 
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.1 }} 
                className="flex flex-col md:flex-row bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:border-[#FF007F]/30 transition-all"
              >
                <div className="md:w-1/2 h-64 md:h-auto relative overflow-hidden">
                  <img src={hotel.image} alt={hotel.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <span className="text-[#FF007F] text-sm uppercase tracking-wider mb-2">{hotel.landmark}</span>
                  <h3 className="font-['Playfair_Display'] text-white text-3xl font-bold mb-2">{hotel.name}</h3>
                  <p className="text-white/60 mb-4">{hotel.city}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-[#FF007F] text-2xl font-bold">{hotel.price}<span className="text-white/40 text-sm font-normal"> /night</span></p>
                    <button className="bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white px-5 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,0,127,0.4)] transition-all">
                      View Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== FAMILY RESORTS ==================== */}
      {!searched && activeCategory === 'family' && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader eyebrow="Fun for Everyone" title="Family Resorts" subtitle="Create lasting memories with your loved ones" accentColor="#FFD700" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FAMILY_RESORTS.map((resort, index) => (
              <motion.div 
                key={resort.name} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.1 }} 
                className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:border-[#FFD700]/30 transition-all"
              >
                <div className="h-48 relative overflow-hidden">
                  <img src={resort.image} alt={resort.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#FFD700] text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{resort.tag}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-white text-xl font-bold mb-1">{resort.name}</h3>
                  <p className="text-white/60 text-sm mb-2">{resort.city}</p>
                  <p className="text-white/80 text-sm">{resort.attraction}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== WELLNESS RETREATS ==================== */}
      {!searched && activeCategory === 'wellness' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader eyebrow="Rejuvenate Your Soul" title="Wellness Retreats" subtitle="Transformative journeys for mind, body, and spirit" accentColor="#00E5FF" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {WELLNESS_RETREATS.map((retreat, index) => (
              <motion.div 
                key={retreat.name} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.15 }} 
                className="relative overflow-hidden rounded-3xl cursor-pointer group"
              >
                <div className="aspect-[4/5] relative">
                  <img src={retreat.image} alt={retreat.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute top-6 left-6 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-[#00E5FF]" />
                    <span className="text-[#00E5FF] text-sm font-medium uppercase tracking-wider">Wellness</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <span className="text-white/60 text-sm">{retreat.duration}</span>
                    <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold mt-1 mb-2">{retreat.name}</h3>
                    <p className="text-white/60 mb-2">{retreat.location}</p>
                    <p className="text-[#00E5FF] font-medium">{retreat.focus}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== TRAVEL STORIES ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-20 bg-[#0D0B14]">
          <SectionHeader eyebrow="Stay Stories" title="Editorial Inspiration" subtitle="Curated stories about the world's most extraordinary stays" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STAY_STORIES.map((story, index) => (
              <motion.article 
                key={story.title} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: index * 0.15 }} 
                className="group cursor-pointer"
              >
                <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-5 relative">
                  <img src={story.image} alt={story.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">{story.category}</span>
                  </div>
                </div>
                <span className="text-white/40 text-sm">{story.readTime}</span>
                <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold mt-2 mb-3 group-hover:text-[#FF007F] transition-colors">{story.title}</h3>
                <p className="text-white/60 text-sm">By {story.author}</p>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* ==================== INSPIRATION QUOTE ==================== */}
      {!searched && activeCategory !== 'explore' && (
        <section className="px-4 md:px-16 py-32 bg-[#09080E] text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Home className="w-12 h-12 text-[#FF007F] mx-auto mb-8" />
            <blockquote className="font-['Playfair_Display'] text-3xl md:text-5xl text-white font-bold max-w-4xl mx-auto leading-tight mb-8">
              "A room is just a room until you add your story to it."
            </blockquote>
            <p className="text-white/50 uppercase tracking-widest">— Unknown</p>
          </motion.div>
        </section>
      )}

      {/* ==================== EXPLORE PLACES (Foursquare Integration) ==================== */}
      {activeCategory === 'explore' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader 
            eyebrow="Powered by Foursquare" 
            title="Explore Places" 
            subtitle="Discover hotels, restaurants, attractions and more around the world" 
            accentColor="#00E5FF" 
          />
          <PlacesSearch 
            onPlaceSelect={(place) => setSelectedPlace(place)}
            initialLocation={searchForm.destination}
          />
        </section>
      )}

      {/* ==================== WEATHER INTELLIGENCE (OpenWeather Integration) ==================== */}
      {activeCategory === 'weather' && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader 
            eyebrow="Powered by OpenWeather" 
            title="AIRA Weather Intelligence" 
            subtitle="Plan your perfect trip with real-time weather forecasts, travel recommendations, and packing suggestions" 
            accentColor="#FFD700" 
          />
          <WeatherSearchSection onWeatherLoad={setDestinationWeather} />
          {destinationWeather && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8"
            >
              <WeatherRecommendations weather={destinationWeather} />
            </motion.div>
          )}
        </section>
      )}

      {/* Place Details Modal */}
      {selectedPlace && (
        <PlaceDetails 
          place={selectedPlace} 
          onClose={() => setSelectedPlace(null)} 
        />
      )}

      {/* AI Travel Concierge */}
      <TravelConcierge 
        onStaySearch={handleConciergeStaySearch}
        onHotelSearch={handleConciergeStaySearch}
        currentPage="stays"
      />
    </div>
  );
}
